/**
 * SpecsSection Component
 * 伺服器規格配置區塊
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7
 */

import type { DiskConfig, CategoryType } from '../../types/card';
import { routeOptions } from '../../types/card';
import type { Translations } from '../../i18n';

interface SpecsSectionProps {
  category: CategoryType;
  cpu: string;
  ram: string;
  disks: DiskConfig[];
  routeType: string;
  customRoute: string;
  onCpuChange: (value: string) => void;
  onRamChange: (value: string) => void;
  onDisksChange: (disks: DiskConfig[]) => void;
  onRouteTypeChange: (value: string) => void;
  onCustomRouteChange: (value: string) => void;
  t: Translations;
}

const diskTypes = ['SSD', 'NVMe', 'HDD', 'SAS'] as const;
const diskUnits = ['GB', 'TB'] as const;

/** 判斷是否需要線路類型（VPS/VDS 需要，虛擬主機不需要） */
function needsRouteType(category: CategoryType): boolean {
  return category === 'vps' || category === 'vds';
}

export function SpecsSection({
  category,
  cpu,
  ram,
  disks,
  routeType,
  customRoute,
  onCpuChange,
  onRamChange,
  onDisksChange,
  onRouteTypeChange,
  onCustomRouteChange,
  t,
}: SpecsSectionProps) {
  const handleAddDisk = () => {
    onDisksChange([...disks, { size: 10, unit: 'GB', type: 'SSD' }]);
  };

  const handleRemoveDisk = (index: number) => {
    if (disks.length > 1) {
      onDisksChange(disks.filter((_, i) => i !== index));
    }
  };

  const handleDiskChange = (index: number, field: keyof DiskConfig, value: string | number) => {
    const newDisks = [...disks];
    newDisks[index] = { ...newDisks[index], [field]: value };
    onDisksChange(newDisks);
  };

  const showRouteType = needsRouteType(category);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">{t.specsConfig}</h3>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.cpu}</label>
          <input
            type="text"
            value={cpu}
            onChange={(e) => onCpuChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="1 vCPU AMD EPYC"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.ram}</label>
          <input
            type="text"
            value={ram}
            onChange={(e) => onRamChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="1 GB"
          />
        </div>
      </div>

      {/* 硬碟配置 */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">{t.diskConfig}</label>
          <button
            type="button"
            onClick={handleAddDisk}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {t.addDisk}
          </button>
        </div>
        
        <div className="space-y-2">
          {disks.map((disk, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="number"
                value={disk.size}
                onChange={(e) => handleDiskChange(index, 'size', parseInt(e.target.value) || 0)}
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="0"
              />
              <select
                value={disk.unit}
                onChange={(e) => handleDiskChange(index, 'unit', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {diskUnits.map((unit) => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
              <select
                value={disk.type}
                onChange={(e) => handleDiskChange(index, 'type', e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {diskTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              {disks.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveDisk(index)}
                  className="text-red-500 hover:text-red-700 p-1"
                  title="刪除硬碟"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* 線路類型 - 只有 VPS/VDS 需要 */}
      {showRouteType && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.routeType}</label>
          <select
            value={routeType}
            onChange={(e) => onRouteTypeChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {routeOptions.map((route) => (
              <option key={route} value={route}>
                {route === 'custom' ? t.custom : route}
              </option>
            ))}
          </select>
          
          {routeType === 'custom' && (
            <input
              type="text"
              value={customRoute}
              onChange={(e) => onCustomRouteChange(e.target.value)}
              className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t.custom}
            />
          )}
        </div>
      )}
    </div>
  );
}
